import { useState, useEffect } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

// Simple client-side rate limiting for authentication
interface AuthRateLimit {
  attempts: number;
  lastAttempt: number;
  blockedUntil?: number;
}

const AUTH_RATE_LIMITS = {
  LOGIN: { maxAttempts: 5, windowMs: 15 * 60 * 1000, blockMs: 30 * 60 * 1000 },
  // More lenient signup limits - 10 attempts per hour, 15 minute block
  SIGNUP: { maxAttempts: 10, windowMs: 60 * 60 * 1000, blockMs: 15 * 60 * 1000 }
};

// Get rate limit data from localStorage
function getRateLimit(type: 'login' | 'signup'): AuthRateLimit {
  try {
    const stored = localStorage.getItem(`auth_rate_limit_${type}`);
    return stored ? JSON.parse(stored) : { attempts: 0, lastAttempt: 0 };
  } catch {
    return { attempts: 0, lastAttempt: 0 };
  }
}

// Update rate limit data
function updateRateLimit(type: 'login' | 'signup', data: AuthRateLimit): void {
  try {
    localStorage.setItem(`auth_rate_limit_${type}`, JSON.stringify(data));
  } catch {
    // Silently fail if localStorage is not available
  }
}

// Check if rate limited
function checkRateLimit(type: 'login' | 'signup'): { allowed: boolean; retryAfter?: number; message?: string } {
  const config = AUTH_RATE_LIMITS[type.toUpperCase() as keyof typeof AUTH_RATE_LIMITS];
  const rateLimit = getRateLimit(type);
  const now = Date.now();
  
  // Check if currently blocked
  if (rateLimit.blockedUntil && rateLimit.blockedUntil > now) {
    const retryAfter = Math.ceil((rateLimit.blockedUntil - now) / 1000);
    return {
      allowed: false,
      retryAfter,
      message: `Too many ${type} attempts. Please try again in ${Math.ceil(retryAfter / 60)} minutes.`
    };
  }
  
  // Reset attempts if window expired
  if (now - rateLimit.lastAttempt > config.windowMs) {
    rateLimit.attempts = 0;
    rateLimit.blockedUntil = undefined;
  }
  
  // Check if limit exceeded
  if (rateLimit.attempts >= config.maxAttempts) {
    const blockedUntil = now + config.blockMs;
    const updatedRateLimit: AuthRateLimit = {
      attempts: rateLimit.attempts,
      lastAttempt: now,
      blockedUntil
    };
    updateRateLimit(type, updatedRateLimit);
    
    const retryAfter = Math.ceil(config.blockMs / 1000);
    return {
      allowed: false,
      retryAfter,
      message: `Too many ${type} attempts. Please try again in ${Math.ceil(retryAfter / 60)} minutes.`
    };
  }
  
  return { allowed: true };
}

// Record attempt
function recordAuthAttempt(type: 'login' | 'signup', success: boolean): void {
  const rateLimit = getRateLimit(type);
  const now = Date.now();
  
  if (success) {
    // Clear rate limit on successful auth
    updateRateLimit(type, { attempts: 0, lastAttempt: 0 });
  } else {
    // Increment failed attempts
    updateRateLimit(type, {
      attempts: rateLimit.attempts + 1,
      lastAttempt: now,
      blockedUntil: rateLimit.blockedUntil
    });
  }
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      const newUser = session?.user ?? null;
      setUser(newUser);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        const newUser = session?.user ?? null;
        setUser(newUser);
        setLoading(false);
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string) => {
    // Check rate limiting before attempting signup
    const rateLimitCheck = checkRateLimit('signup');
    if (!rateLimitCheck.allowed) {
      return { 
        data: null, 
        error: { 
          message: rateLimitCheck.message || 'Rate limited',
          status: 429
        } 
      };
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });
    
    // Record the attempt for rate limiting
    recordAuthAttempt('signup', !error);
    
    return { data, error };
  };

  const signIn = async (email: string, password: string) => {
    // Check rate limiting before attempting login
    const rateLimitCheck = checkRateLimit('login');
    if (!rateLimitCheck.allowed) {
      return { 
        data: null, 
        error: { 
          message: rateLimitCheck.message || 'Rate limited',
          status: 429
        } 
      };
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    // Record the attempt for rate limiting
    recordAuthAttempt('login', !error);
    
    return { data, error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  };

  return {
    user,
    loading,
    signUp,
    signIn,
    signOut,
  };
}